import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import {
  Plus,
  Trash2,
  Copy,
  Power,
  Download,
  Printer,
  X,
} from "lucide-react";
import { http, apiError } from "@/lib/api";
import AdminShell, { PageHead } from "@/components/AdminShell";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

export default function Tables() {
  const [tables, setTables] = useState([]);
  const [name, setName] = useState("");
  const [qrModal, setQrModal] = useState(null);
  const [printMode, setPrintMode] = useState(false);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const base = window.location.origin;
  const modalRef = useRef(null);

  const isOwner = me?.role === "owner";

  async function load() {
    try {
      const [tablesRes, meRes] = await Promise.all([
        http.get("/admin/tables"),
        http.get("/auth/me"),
      ]);

      setTables(tablesRes.data);
      setMe(meRes.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function add() {
    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh menambah meja.");
      return;
    }

    const cleanName = name.trim();

    if (!cleanName) {
      toast.error("Nama meja wajib diisi.");
      return;
    }

    try {
      await http.post("/admin/tables", {
        name: cleanName,
      });

      setName("");
      toast.success("Meja ditambahkan");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function toggle(id) {
    try {
      await http.patch(`/admin/tables/${id}/toggle`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function askDelete(table) {
    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh menghapus meja.");
      return;
    }
    setDeleteTarget(table);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await http.delete(`/admin/tables/${deleteTarget.id}`);
      toast.success("Meja dihapus");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  function link(t) {
    return `${base}/t/${t.token}`;
  }

  async function copyLink(t) {
    try {
      await navigator.clipboard.writeText(link(t));
      toast.success("Link disalin");
    } catch {
      toast.error("Gagal menyalin link");
    }
  }

  function download(t) {
    const canvas = document.querySelector(
      `#qr-dl-${t.id} canvas`
    );

    if (!canvas) {
      toast.error("QR belum siap.");
      return;
    }

    const a = document.createElement("a");

    a.href = canvas.toDataURL("image/png");
    a.download = `QR-${t.name.replace(/\s+/g, "-")}.png`;

    document.body.appendChild(a);
    a.click();
    a.remove();

    toast.success("QR diunduh");
  }

  function printAll() {
    setPrintMode(true);

    setTimeout(() => {
      window.print();

      setTimeout(() => {
        setPrintMode(false);
      }, 300);
    }, 350);
  }

  function printOne() {
    window.print();
  }

  return (
    <AdminShell>
      <PageHead
        eyebrow="Operasional"
        title="Meja & QR"
        desc="Setiap meja punya QR sendiri untuk dipindai pelanggan."
      >
        <button
          data-testid="print-all-btn"
          onClick={printAll}
          className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-bold text-card-foreground transition-transform active:scale-95"
        >
          <Printer size={16} />
          Cetak Semua QR
        </button>
      </PageHead>

      {isOwner && (
        <div className="mb-5 flex gap-2 no-print">
          <input
            data-testid="table-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                add();
              }
            }}
            placeholder="Nama meja (cth Meja 5)"
            className="flex-1 rounded-xl border border-border bg-muted px-4 py-3 text-sm outline-none"
          />

          <button
            data-testid="add-table-btn"
            onClick={add}
            className="flex items-center gap-2 rounded-xl bg-primary px-4 font-bold text-primary-foreground"
          >
            <Plus size={16} />
          </button>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Memuat meja...
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 no-print">
          {tables.map((t) => (
            <div
              key={t.id}
              className="rounded-3xl border border-border bg-card p-4"
            >
              <div className="flex items-start gap-3">
                <button
                  data-testid={`qr-open-${t.id}`}
                  onClick={() => setQrModal(t)}
                  className="grid h-20 w-20 flex-none place-items-center rounded-2xl bg-white p-1.5"
                >
                  <QRCodeCanvas
                    value={link(t)}
                    size={72}
                    level="M"
                  />
                </button>

                <div className="min-w-0 flex-1">
                  <b className="text-sm text-card-foreground">
                    {t.name}
                  </b>

                  <div
                    className={`text-xs ${
                      t.active
                        ? "text-primary"
                        : "text-muted-foreground"
                    }`}
                  >
                    {t.active ? "Aktif" : "Nonaktif"}
                  </div>

                  <div className="mt-1 truncate rounded-lg bg-muted px-2 py-1 text-[10px] text-muted-foreground">
                    /t/{t.token}
                  </div>
                </div>
              </div>

              <div
                id={`qr-dl-${t.id}`}
                className="hidden"
              >
                <QRCodeCanvas
                  value={link(t)}
                  size={512}
                  level="M"
                  includeMargin
                />
              </div>

              <div
                className={`mt-3 grid gap-1.5 ${
                  isOwner
                    ? "grid-cols-4"
                    : "grid-cols-3"
                }`}
              >
                <button
                  onClick={() => download(t)}
                  data-testid={`qr-dl-btn-${t.id}`}
                  className="flex items-center justify-center gap-1 rounded-xl bg-muted py-2 text-[11px] font-bold"
                  title="Download QR"
                >
                  <Download size={13} />
                </button>

                <button
                  onClick={() => copyLink(t)}
                  className="flex items-center justify-center gap-1 rounded-xl bg-muted py-2 text-[11px] font-bold"
                  title="Salin link"
                >
                  <Copy size={13} />
                </button>

                <button
                  onClick={() => toggle(t.id)}
                  className="flex items-center justify-center gap-1 rounded-xl bg-muted py-2 text-[11px] font-bold"
                  title="Aktif/Nonaktif"
                >
                  <Power size={13} />
                </button>

                {isOwner && (
                  <button
                    data-testid={`del-table-${t.id}`}
                    onClick={() => askDelete(t)}
                    className="flex items-center justify-center rounded-xl bg-destructive/10 py-2 text-destructive"
                    title="Hapus"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}

          {tables.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              Belum ada meja.
            </div>
          )}
        </div>
      )}

      {qrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 no-print-modal">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm no-print"
            onClick={() => setQrModal(null)}
          />

          <div
            ref={modalRef}
            className="print-area relative w-full max-w-xs rounded-3xl border border-border bg-card p-6 text-center"
          >
            <button
              onClick={() => setQrModal(null)}
              className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-muted no-print"
            >
              <X size={18} />
            </button>

            <div className="font-heading text-lg font-extrabold text-card-foreground">
              {qrModal.name}
            </div>

            <div className="mt-1 text-xs text-muted-foreground">
              Scan untuk memesan
            </div>

            <div className="mx-auto mt-4 w-fit rounded-2xl bg-white p-3">
              <QRCodeCanvas
                value={link(qrModal)}
                size={200}
                level="M"
                includeMargin
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 no-print">
              <button
                onClick={() => download(qrModal)}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground"
              >
                <Download size={15} />
                Unduh
              </button>

              <button
                onClick={printOne}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-muted py-2.5 text-sm font-bold"
              >
                <Printer size={15} />
                Cetak
              </button>
            </div>
          </div>
        </div>
      )}

      {printMode && (
        <div className="print-sheet fixed inset-0 z-[60] overflow-auto bg-white p-6">
          <h1 className="mb-4 text-center text-xl font-extrabold text-black">
            QR Meja — Warmindo Omee
          </h1>

          <div className="grid grid-cols-3 gap-4">
            {tables.map((t) => (
              <div
                key={t.id}
                className="flex flex-col items-center rounded-xl border border-neutral-300 p-3"
              >
                <div className="text-sm font-bold text-black">
                  {t.name}
                </div>

                <div className="my-2">
                  <QRCodeCanvas
                    value={link(t)}
                    size={150}
                    level="M"
                    includeMargin
                  />
                </div>

                <div className="text-[10px] text-neutral-500">
                  Scan untuk memesan
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        title="Hapus Meja?"
        message="Meja yang sudah memiliki riwayat pesanan tidak dapat dihapus dan sebaiknya dinonaktifkan."
        itemName={deleteTarget?.name || ""}
        busy={deleteBusy}
        onCancel={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }

          .print-sheet,
          .print-sheet * {
            visibility: visible !important;
          }

          .print-sheet {
            position: absolute;
            inset: 0;
          }

          .no-print-modal .print-area,
          .no-print-modal .print-area * {
            visibility: visible !important;
          }

          .no-print-modal .print-area {
            position: absolute;
            left: 50%;
            top: 40px;
            transform: translateX(-50%);
          }

          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </AdminShell>
  );
}
